import React from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PayPeriodSettingsEditor from "./PayPeriodSettingsEditor";
import BusinessSettingsEditor from "./BusinessSettingsEditor";
import RoleColorsEditor from "../department_role_station_settings/RoleColorsEditor";
import CheckInTemplateSettingsDisplayConfigurations from "../configurations/CheckInTemplateSettingsDisplayConfigurations";
import DepartmentRoleStationSettingsDisplay from "../department_role_station_settings/DepartmentRoleStationSettingsDisplay";
import TrainingProficiencyDisplay from "../training_overview/TrainingProficiencyDisplay";
import TipPoolsSettingsEditor from "./TipPoolsSettingsEditor";
import { getFeatureSettingEditors, isFeatureEditorEnabled } from "./featureSettingsCatalog";

function FeatureEditor({ flat, editor, companyId, location, storeNames, onDirtyChange, onSaved, renderLocationEditor, permissionRole, opsRefreshSignal, active = true }) {
  const companyStores = (storeNames || []).filter((store) => Number(store.company) === Number(companyId));
  const dirtyKey = `${companyId}:${location?.id || "defaults"}:${editor.id}`;
  const reportDirty = React.useCallback(
    (dirty) => onDirtyChange?.(dirtyKey, dirty),
    [dirtyKey, onDirtyChange]
  );
  React.useEffect(() => () => reportDirty(false), [reportDirty]);

  if (editor.id === "pay-periods") return <PayPeriodSettingsEditor companyId={companyId} storeId={location?.id} onDirtyChange={reportDirty} />;
  if (editor.locationEditor) return renderLocationEditor?.(editor.id, location);
  if (editor.group) {
    return <BusinessSettingsEditor
      compact={flat}
      group={editor.group}
      companyId={companyId}
      storeId={editor.scope === "location" ? location.id : undefined}
      onDirtyChange={reportDirty}
      onSaved={onSaved}
    />;
  }
  if (editor.id === "check-ins") {
    return <CheckInTemplateSettingsDisplayConfigurations homeCompany={companyId} embedded={flat} />;
  }
  const anchorStore = location || companyStores[0];
  if (editor.id === "roles") {
    if (!anchorStore) return <p className="p-4 text-sm text-slate-500">Add a location to configure team structure.</p>;
    return <DepartmentRoleStationSettingsDisplay embedded companyScope
      homeStore={anchorStore.id} homeCompany={companyId} storeNames={storeNames}
      permissionRole={permissionRole} opsRefreshSignal={opsRefreshSignal} active={active}
    />;
  }
  if (editor.id === "role-colors") return <RoleColorsEditor companyId={companyId} storeId={location.id} />;
  if (editor.id === "tip-pools") return <TipPoolsSettingsEditor companyId={companyId} permissionRole={permissionRole} onDirtyChange={reportDirty} active={active} />;

  return <TrainingProficiencyDisplay
    homeStore={anchorStore?.id}
    storeNames={storeNames}
    proficiencyStarsCount={anchorStore?.company_info?.proficiency_stars_count || 5}
  />;
}

export default function FeatureSettings({ flat = false, editorId, activeEditorId, hideHeading = false, scopeFilter, bucketKey, flags, isCompanyDefault, companyId, storeNames, onDirtyChange, onSaved, onOpenLaborModel, renderLocationEditor, permissionRole, opsRefreshSignal, focusEditor, active = true }) {
  // Keep opened editors mounted when folded up so local drafts survive. There
  // are no requests until the user opens an editor for the first time.
  const [open, setOpen] = React.useState({});
  const [visited, setVisited] = React.useState({});
  const editors = getFeatureSettingEditors(bucketKey, isCompanyDefault).filter((editor) =>
    (!editorId || editor.id === editorId) && (!scopeFilter || editor.scope === scopeFilter || editor.workspace)
  );
  const focusId = focusEditor?.editorId;
  const focusAt = focusEditor?.at;
  React.useEffect(() => {
    if (!focusId || !editors.some((editor) => editor.id === focusId)) return;
    setOpen((previous) => ({ ...previous, [focusId]: true }));
    setVisited((previous) => ({ ...previous, [focusId]: true }));
    const id = `feature-editor-${companyId}-${isCompanyDefault ? "defaults" : flags.id}-${focusId}`;
    const frame = window.requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: "start" }));
    return () => window.cancelAnimationFrame(frame);
    // The timestamp represents a new navigation request, not a data refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, focusAt]);

  React.useEffect(() => {
    if (!flat) return;
    setVisited((previous) => {
      const enabledIds = editors.filter((editor) => !editor.workspace && isFeatureEditorEnabled(editor, flags)).map((editor) => editor.id);
      if (enabledIds.every((id) => previous[id])) return previous;
      return { ...previous, ...Object.fromEntries(enabledIds.map((id) => [id, true])) };
    });
  }, [flat, editors, flags]);

  if (flat) return <div>
    {editors.map((editor) => {
      const enabled = isFeatureEditorEnabled(editor, flags);
      if (editor.workspace) return <button key={editor.id} type="button" disabled={!enabled}
        className="mt-5 rounded-lg border border-violet-200 px-3 py-2 text-sm font-medium text-violet-700 disabled:opacity-50"
        onClick={() => onOpenLaborModel?.({ companyId, storeId: isCompanyDefault ? undefined : flags.id })}>{editor.title}</button>;
      return <section key={editor.id} hidden={Boolean(activeEditorId && activeEditorId !== editor.id)} data-setting-editor={editor.id} id={scopeFilter === "company" ? `company-setting-${companyId}-${bucketKey}-${editor.id}` : `feature-editor-${companyId}-${isCompanyDefault ? "defaults" : flags.id}-${editor.id}`} className={hideHeading ? "" : bucketKey === "location_details" ? "mt-6 scroll-mt-24" : "mt-6 scroll-mt-24 border-t border-slate-200 pt-4"}>
        {!hideHeading && <h3 className="mb-3 text-sm font-semibold text-slate-900">{editor.title}</h3>}
        {!hideHeading && editor.id === "roles" && <p className="mb-3 text-xs text-slate-500">Manage company-wide departments, roles, stations, and default role colors.</p>}
        {!enabled && <p className="mb-3 text-xs text-slate-500">Enable the related feature at a location to use these settings.</p>}
        {(enabled || visited[editor.id]) && <FeatureEditor flat editor={editor} companyId={companyId} location={isCompanyDefault || scopeFilter === "company" ? null : flags}
          storeNames={storeNames} onDirtyChange={onDirtyChange} onSaved={onSaved} renderLocationEditor={renderLocationEditor}
          permissionRole={permissionRole} opsRefreshSignal={opsRefreshSignal} active={active} />}
      </section>;
    })}
    {bucketKey === "scheduling" && isCompanyDefault && <p className="mt-5 text-xs text-slate-500">Request notice, break defaults, and calendar boundaries are configured per location. Select a location’s Scheduling cell to edit them.</p>}
  </div>;

  return <div className="divide-y divide-slate-100">
    {editors.map((editor) => {
      const enabled = isFeatureEditorEnabled(editor, flags);
      const expanded = Boolean(open[editor.id]);
      const regionId = `feature-editor-${companyId}-${isCompanyDefault ? "defaults" : flags.id}-${editor.id}`;
      return <div key={editor.id}>
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 rounded px-1 py-3 text-left text-sm hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-600 disabled:cursor-not-allowed disabled:opacity-50"
          disabled={!enabled && !visited[editor.id]}
          aria-expanded={editor.workspace ? undefined : expanded}
          aria-controls={editor.workspace ? undefined : regionId}
          onClick={() => {
            if (editor.workspace) {
              onOpenLaborModel?.({ companyId, storeId: isCompanyDefault ? undefined : flags.id });
              return;
            }
            setVisited((previous) => ({ ...previous, [editor.id]: true }));
            setOpen((previous) => ({ ...previous, [editor.id]: !previous[editor.id] }));
          }}
        >
          <span>
            <span className="block font-medium text-slate-800">{editor.title}</span>
            <span className="mt-1 block text-xs text-slate-500">
              {editor.scopeDescription || (editor.scope === "company" ? "Company-wide · affects all locations" : `This location · ${flags.store_name}`)}
              {!enabled && " · enable the feature to configure"}
            </span>
          </span>
          {editor.workspace ? <OpenInNewIcon fontSize="small" className="text-slate-400" /> : expanded ? <ExpandLessIcon fontSize="small" className="text-slate-400" /> : <ExpandMoreIcon fontSize="small" className="text-slate-400" />}
        </button>
        {!editor.workspace && <div id={regionId} hidden={!expanded}>
          {visited[editor.id] && <div className="mb-3 rounded-lg border border-slate-200 bg-white">
            {editor.scope === "company" && <p className="border-b border-violet-100 bg-violet-50 px-4 py-3 text-xs text-violet-800">
              {editor.scopeHelp || "These settings apply across the company. They are not part of the defaults for new locations."}
            </p>}
            <FeatureEditor
              editor={editor}
              companyId={companyId}
              location={isCompanyDefault ? null : flags}
              storeNames={storeNames}
              onDirtyChange={onDirtyChange}
              onSaved={onSaved}
              renderLocationEditor={renderLocationEditor}
              permissionRole={permissionRole}
              opsRefreshSignal={opsRefreshSignal}
              active={active}
            />
          </div>}
        </div>}
      </div>;
    })}
    {bucketKey === "scheduling" && isCompanyDefault && <p className="py-3 text-xs text-slate-500">
      Configure request notice, break defaults, and calendar boundaries in each location’s Scheduling section below.
    </p>}
  </div>;
}
